import type { AccentHue } from "@repo/fika-design-system/color"
import { For, type JSX, Show } from "solid-js"
import { ResponsiveImage } from "@/designSystem/components/responsive-image"
import { Text } from "@/designSystem/components/text"
import { HumanContentBadge } from "@/designSystem/patterns/human-content-badge"
import { graphql } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import { PublishedPostReactionSummaryFragment } from "@/lib/get-reaction-summary"
import { ContentAccessBadgeFragment } from "@/pages/subscriptions/content-access-badge"
import {
  cardBadgesClass,
  cardContentClass,
  cardDateRecipe,
  cardDescriptionRecipe,
  cardExcerptClass,
  cardHeaderClass,
  cardImageRecipe,
  cardMetaClass,
  cardRecipe,
  cardTitleRecipe,
  dotSeparatorRecipe,
  publicationAvatarClass,
  publicationHeaderClass,
  publicationHeaderContainerClass,
  publicationNameRecipe,
  reactionCountClass,
  reactionEmojiClass,
  reactionsClass,
} from "./post-card.css"

export const PostCardFragment = graphql(
  `fragment PostCard on PublishedPost {
    id
    title
    description
    content
    url
    imageUrl
    firstPublishedAt
    featuredAt
    humanContent
    ...ContentAccessBadge
    ...PublishedPostReactionSummary
    publication {
      id
      name
      title
      logoUrl
      enableMonetization
      subscriptionTiers {
        id
        name
        description
        monthlyPriceCents
        annualPriceCents
        canPurchase
      }
      sub {
        id
        tier {
          id
          name
        }
      }
      author {
        id
        authorName
        profileImage
      }
    }
  }`,
  [ContentAccessBadgeFragment, PublishedPostReactionSummaryFragment],
)

export function PostCard(props: {
  hue: AccentHue
  href: string
  target?: string
  publicationAvatarUrl: string
  publicationName: string
  imageUrl?: string | null
  imageSrc?: string | null
  title: string
  description?: string | null
  excerpt?: string | null
  publishedDate?: string | null
  reactions?: {
    totalCount: number
    topEmojis: string[]
  }
  isHumanContent?: boolean
  footerAction?: JSX.Element
}) {
  const { LL } = useI18nContext()

  return (
    <a
      href={props.href}
      target={props.target}
      rel={props.target === "_blank" ? "noopener noreferrer" : undefined}
      class={cardRecipe({})}
    >
      <div class={cardHeaderClass}>
        <div class={publicationHeaderClass}>
          <div class={publicationHeaderContainerClass}>
            <ResponsiveImage
              class={publicationAvatarClass}
              image={{
                url: props.publicationAvatarUrl,
                imageClass: "logo",
              }}
              alt={props.publicationName}
            />
            <span class={publicationNameRecipe({ hue: props.hue })}>
              {props.publicationName}
            </span>
          </div>
        </div>
        <Show when={props.isHumanContent}>
          <div class={cardBadgesClass}>
            <Show when={props.isHumanContent}>
              <HumanContentBadge />
            </Show>
          </div>
        </Show>
      </div>

      <div class={cardContentClass}>
        <div
          style={{ display: "flex", "flex-direction": "column", gap: "2px" }}
        >
          <span class={cardTitleRecipe({ hue: props.hue })}>{props.title}</span>
          <Show when={props.description}>
            {description => (
              <span class={cardDescriptionRecipe({ hue: props.hue })}>
                {description()}
              </span>
            )}
          </Show>
          <Show when={props.imageSrc}>
            {src => (
              <img
                src={src()}
                alt={props.title}
                class={cardImageRecipe({ hue: props.hue })}
                loading="lazy"
              />
            )}
          </Show>
        </div>
        <Show when={props.excerpt}>
          {text => (
            <Text
              fontSize="readable"
              color={`${props.hue}900`}
              lineClamp="3"
              fontFamily="serif"
              class={cardExcerptClass}
            >
              {text()}
            </Text>
          )}
        </Show>
      </div>

      <Show when={props.footerAction}>{action => action()}</Show>

      <div class={cardMetaClass}>
        <Show when={props.publishedDate}>
          {date => (
            <time class={cardDateRecipe({ hue: props.hue })}>{date()}</time>
          )}
        </Show>
        <Show
          when={
            props.publishedDate &&
            props.reactions &&
            props.reactions.totalCount > 0
          }
        >
          <span class={dotSeparatorRecipe({ hue: props.hue })} />
        </Show>
        <Show when={props.reactions && props.reactions.totalCount > 0}>
          <span class={reactionsClass}>
            <For each={props.reactions?.topEmojis}>
              {emoji => <span class={reactionEmojiClass}>{emoji}</span>}
            </For>
            <span class={reactionCountClass}>
              {LL().common.countReactions(props.reactions!.totalCount)}
            </span>
          </span>
        </Show>
      </div>
    </a>
  )
}
